import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import Webcam from "react-webcam";
import { Loader, Text, Button, useMantineTheme } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { IconFaceId } from "@tabler/icons-react";
import { isMobile } from "react-device-detect";
import useFaceDetection, { AUTO_CAPTURE_DELAY_MS } from "../../../hooks/useFaceDetection";
import { trackEvent } from "../../../utils/telemetry";
import { measureSharpness } from "../../../utils/imageQuality";
import { flagEnabled, landscapeEnabled, FACE_BEST_FRAME } from "../../../utils/featureFlags";
import { useDeclareLandscapeSupport, currentOrientation } from "../../../hooks/useOrientation";
import { collectCameraSignals } from "../../../utils/captureIntegrity";
import useLocale from "../../../hooks/useLocale";
import AndroidCameraBlockPortal from "../../../components/AndroidCameraBlockPortal";
import { getAndroidBlockVariant, shouldShowAndroidCameraBlock } from "../../../utils/androidDetection";
import HoldRing from "../../../components/hold-ring";
import { IconCameraOff } from "@tabler/icons-react";

const STYLE_ID = "veratad-face-capture-styles";
// Hold-timer ring (see components/hold-ring.js): distance in px from
// the oval border to the ring, stroke width, and animation variant.
const HOLD_RING_GAP = 10;
const HOLD_RING_THICKNESS = 5;
const HOLD_RING_VARIANT = "sweep";
// Gap in px between the bottom of the oval and the feedback line.
const FEEDBACK_OFFSET = 30;

function injectStyles(glowColor) {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    :root { --glow-color: ${glowColor}; }

    @keyframes glowPulse {
      0%, 100% {
        box-shadow:
          0 0 calc(6px * var(--glow-intensity)) calc(2px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(18px * var(--glow-intensity)) calc(5px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(35px * var(--glow-intensity)) calc(10px * var(--glow-intensity)) var(--glow-color);
        opacity: calc(0.3 + 0.14 * var(--glow-intensity));
      }
      50% {
        box-shadow:
          0 0 calc(12px * var(--glow-intensity)) calc(4px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(30px * var(--glow-intensity)) calc(10px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(60px * var(--glow-intensity)) calc(18px * var(--glow-intensity)) var(--glow-color);
        opacity: calc(0.4 + 0.12 * var(--glow-intensity));
      }
    }

    @keyframes glowLocked {
      0% {
        box-shadow:
          0 0 calc(14px * var(--glow-intensity)) calc(4px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(35px * var(--glow-intensity)) calc(10px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(60px * var(--glow-intensity)) calc(18px * var(--glow-intensity)) var(--glow-color);
      }
      100% {
        box-shadow:
          0 0 calc(10px * var(--glow-intensity)) calc(3px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(25px * var(--glow-intensity)) calc(8px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(45px * var(--glow-intensity)) calc(14px * var(--glow-intensity)) var(--glow-color);
      }
    }
  `;
  document.head.appendChild(style);
}

// Oval geometry that eases toward its target instead of jumping, so a
// layout change (landscape intro -> live capture, a rotation) slides
// the oval, its mask, glow and hold ring together. Driven from JS
// because Safari does not transition SVG ellipse geometry from CSS.
// With animate off it returns the target as-is (no lag, no extra
// renders), which keeps portrait behavior exactly as before.
const OVAL_KEYS = ["cx", "cy", "rx", "ry"];
const OVAL_TWEEN_MS = 700;
function useTweenedOval(target, animate) {
  const [value, setValue] = useState(target);
  const valueRef = useRef(target);
  useEffect(() => {
    const from = valueRef.current;
    const settled = OVAL_KEYS.every((k) => Math.abs(from[k] - target[k]) < 0.5);
    // Never ease from a non-finite start (e.g. the first render, before
    // the viewport has been measured): it would stay NaN all the way.
    // A hidden page gets no animation frames, so jump straight there.
    const fromValid = OVAL_KEYS.every((k) => Number.isFinite(from[k]));
    const hidden = typeof document !== "undefined" && document.hidden;
    if (!animate || settled || !fromValid || hidden) {
      // Keep the state in step too, so the render where animate flips
      // on starts from where the oval really is.
      valueRef.current = target;
      setValue(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / OVAL_TWEEN_MS);
      // ease-in-out cubic: a slow, even glide rather than a snap
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const next = {};
      OVAL_KEYS.forEach((k) => {
        next[k] = from[k] + (target[k] - from[k]) * e;
      });
      valueRef.current = next;
      setValue(next);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target.cx, target.cy, target.rx, target.ry, animate]);
  return animate ? value : target;
}

function removeStyles() {
  const el = document.getElementById(STYLE_ID);
  if (el) el.remove();
}

export default function VeratadCapture({
  onCapture,
  onError,
  onReady,
  onCameraAllowed,
  onPhotoFallback,
  requestId = null,
  paused = false,
  // Push the face-detection gating state up so a parent (e.g.
  // AgeEstimation's drawer) can render a live checklist. Fires
  // whenever the gating object changes shape. No-op if undefined.
  onGatingChange,
  // Push the resolved feedback message up so a parent can render
  // it in its own UI (e.g. drawer header).
  onFeedbackChange,
  // When true, suppress the in-capture feedback text overlay
  // (parent is rendering it elsewhere). Camera UI still shows.
  hideInCameraFeedback = false,
  // Height in px of anything a parent overlays across the bottom of
  // the viewport (e.g. AgeEstimation's live drawer). The oval, the
  // feedback text and the manual-capture button are laid out in the
  // space above it so none of them end up underneath.
  bottomInset = 0,
  // Width in px of anything a parent docks down the right edge in
  // landscape (AgeEstimation's check column). The oval stays centered
  // on the frame regardless (see the oval sizing below) and shrinks if
  // it would reach under it, so the parent should keep it narrow.
  rightInset = 0,
  // Whether the parent's own chrome works sideways. False keeps this
  // screen portrait-only (rotate prompt up) even with FACE_LANDSCAPE on.
  allowLandscape = true,
}) {
  const theme = useMantineTheme();
  const locale = useLocale("face-capture");
  const { width: vw, height: vh } = useViewportSize();
  const [landscapeFlag] = useState(landscapeEnabled);
  const landscapeCapable = landscapeFlag && allowLandscape;
  // Keep the rotate-your-device prompt down only while the camera is
  // live; a paused mount is still the parent's (portrait) start screen.
  useDeclareLandscapeSupport(landscapeCapable && !paused);
  const landscape = landscapeCapable && vw > vh;
  const webcamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [androidOsBlock, setAndroidOsBlock] = useState(false);
  const cameraRespondedRef = useRef(false);

  // Log mount and permission state for debugging
  useEffect(() => {
    if (paused) {
      console.log("[VeratadCapture] Mounted in paused state — camera will not start until paused=false");
      return;
    }
    console.log("[VeratadCapture] Component MOUNTED, starting camera flow");
    console.log("[VeratadCapture] onError prop:", typeof onError);
    console.log("[VeratadCapture] onReady prop:", typeof onReady);
    trackEvent("camera_requested", {}, { once: true });

    // Check permissions API if available — on Android, detect OS-level camera block
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "camera" })
        .then((status) => {
          console.log("[VeratadCapture] Permission API state:", status.state);
          if (shouldShowAndroidCameraBlock() && status.state === "denied") {
            console.warn("[VeratadCapture] Camera pre-denied on Android — likely OS camera toggle or Chrome permission blocked");
            cameraRespondedRef.current = true;
            setCameraBlocked(true);
            setAndroidOsBlock(true);
            removeStyles();

            window.dispatchEvent(new CustomEvent("veratad-camera-error"));
            if (onError) {
              onError({ type: "camera_error", name: "NotAllowedError", message: "Permission denied" });
            }
            if (typeof window.__veratadCameraError === "function") {
              window.__veratadCameraError();
            }
          }
          status.onchange = () => {
            console.log("[VeratadCapture] Permission state CHANGED to:", status.state);
          };
        })
        .catch((err) => {
          console.log("[VeratadCapture] Permission API not supported:", err.message);
        });
    } else {
      console.log("[VeratadCapture] Permission API not available");
    }

    return () => {
      console.log("[VeratadCapture] Component UNMOUNTING, cameraResponded:", cameraRespondedRef.current);
    };
    // Re-run when `paused` flips so the permission probe + log fire
    // at the moment the camera actually starts, not at original mount.
  }, [paused]);

  // Timeout: if getUserMedia neither resolves nor rejects (e.g. iOS Safari
  // "Cancel" dismisses the prompt without firing either callback), treat it
  // as a camera error so the UI doesn't hang indefinitely.
  // Stable ref for onError so the timeout doesn't need it as a dependency
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    // Don't arm the camera-permission timeout until the Webcam
    // actually mounts — otherwise a paused capture (e.g.
    // AgeEstimation showing the start drawer while waiting on the
    // user) would falsely fire the 7-second error.
    if (paused) {
      return;
    }
    console.log("[VeratadCapture] Timeout effect registered (7s)");
    const timer = setTimeout(() => {
      console.log("[VeratadCapture] 7s timeout fired — cameraResponded:", cameraRespondedRef.current, "cameraReady:", cameraReady);
      if (!cameraRespondedRef.current) {
        console.warn("[VeratadCapture] Camera permission timeout — no response after 7s");
        trackEvent("camera_permission_timeout");

        // Self-close: remove the portal immediately
        setCameraFailed(true);
        removeStyles();

        // Dispatch DOM event to parent
        window.dispatchEvent(new CustomEvent("veratad-camera-error"));

        if (onErrorRef.current) {
          onErrorRef.current({
            type: "camera_error",
            name: "TimeoutError",
            message: "Camera permission prompt timed out",
          });
        }
        if (typeof window.__veratadCameraError === "function") {
          window.__veratadCameraError();
        }
      }
    }, 7000);
    return () => clearTimeout(timer);
  }, [paused]);

  const primaryColor =
    theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5];

  const {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    setOnAutoCapture,
    detectionStalled,
    getFrameChain,
    getLastFrameMetrics,
    getBestFrame,
  } = useFaceDetection(webcamRef, cameraReady);

  // Push gating state up whenever any of the 5 gates (or the
  // room-darkness flag) flips so the parent's drawer checklist
  // stays in sync with what the user is doing in front of the
  // camera. `isTooDark` is folded into the same object so a
  // parent's lighting check can react to overall room brightness
  // even before a face is in frame (the face-region `lighting`
  // gate only flips once `facePresent` does). JSON.stringify dep
  // is fine — the object is tiny and shallow.
  useEffect(() => {
    if (typeof onGatingChange === "function") {
      onGatingChange({ ...gatingState, isTooDark });
    }
  }, [JSON.stringify(gatingState), isTooDark]);

  // Push the resolved feedback message up too so the parent can
  // render it (e.g. as a drawer header). Resolved string, not the
  // raw key, since the parent shouldn't have to repeat the locale
  // lookup capture.js already did.
  useEffect(() => {
    if (typeof onFeedbackChange === "function") {
      const message =
        locale?.feedback?.[feedbackKey] || feedbackKey || "";
      onFeedbackChange(message);
    }
  }, [feedbackKey]);

  const feedbackMessage = locale?.feedback?.[feedbackKey] || feedbackKey;

  // Detection stalled (watchdog fired): the user gets a manual shutter
  // instead of waiting on auto-capture that will never trigger.
  useEffect(() => {
    if (detectionStalled) {
      trackEvent("manual_capture_shown", { reason: "detection_stalled" }, { once: true });
    }
  }, [detectionStalled]);

  // Count how many gates are passing (0-5) to drive glow intensity
  const passingCount = Object.values(gatingState).filter(Boolean).length;

  // Oval sizing. The natural radii are what every flow has always
  // used; they set the oval's shape. When a parent overlays the
  // bottom of the viewport (bottomInset), the oval is laid out in the
  // height left above it, and if it would not fit there together with
  // its hold-timer ring above and its feedback line below, both radii
  // are scaled down by the same factor so the oval gets smaller
  // without changing shape.
  //
  // Landscape (FACE_LANDSCAPE, phone wider than tall): height is the
  // scarce side. The oval sits dead center on the screen, which with
  // objectFit: cover is the center of the video frame, because the
  // centering gate (useFaceDetection) measures against the FRAME
  // center, not the oval. An oval moved off center (the first cut put
  // it left of AgeEstimation's panel) left a face inside the oval
  // ~17% off center against an 18% limit, so the gate flickered and
  // the 1.8s hold never completed. The oval is as tall as the ring
  // allows and keeps the portrait 1.35 height:width ratio; the
  // feedback line and manual shutter go in the gutter left of it and
  // parent chrome (rightInset) stays in the gutter right of it.
  // Desktop keeps its fixed oval: it has always been wider than tall.
  const availH = Math.max(vh - bottomInset, 0);
  const landscapeMobile = landscape && isMobile;
  // While paused (the parent's intro) nothing is being detected, so the
  // placeholder oval centers in the space left of the parent's panel
  // instead, and slides to the frame center when capture starts.
  const introOffCenter = landscapeMobile && paused && rightInset > 0;
  const landscapeHalfW = introOffCenter ? (vw - rightInset) / 2 : vw / 2;
  const landscapeRy = Math.max(
    Math.min(
      availH / 2 - HOLD_RING_GAP - 8,
      // Keep the oval and its ring clear of the right column.
      (landscapeHalfW - (introOffCenter ? 0 : rightInset) - HOLD_RING_GAP - 12) * 1.35,
      260,
    ),
    0,
  );
  const naturalRx = landscapeMobile
    ? landscapeRy / 1.35
    : isMobile ? Math.min(vw * 0.38, 170) : 160;
  const naturalRy = landscapeMobile
    ? landscapeRy
    : isMobile ? Math.min(vh * 0.26, 230) : 215;
  const cyTarget = availH * (landscapeMobile ? 0.5 : 0.40);
  // Room above the center for the oval plus the ring outside it, and
  // below it for the oval, the feedback line's offset and whatever
  // sits in that slot: one line of text, or, once detection has
  // stalled and the manual shutter is up, the hint plus the button.
  // In landscape the manual shutter sits beside the oval, not under it.
  const belowOvalReserve =
    detectionStalled && bottomInset > 0 && !landscapeMobile ? 110 : 40;
  const ryFitsAbove = cyTarget - HOLD_RING_GAP - 4;
  const ryFitsBelow = landscapeMobile
    ? ryFitsAbove
    : availH - cyTarget - FEEDBACK_OFFSET - belowOvalReserve;
  // naturalRy is 0 until the viewport has been measured; 0/0 would
  // make every radius NaN.
  const ovalScale =
    naturalRy > 0
      ? Math.min(1, Math.max(0, Math.min(ryFitsAbove, ryFitsBelow)) / naturalRy)
      : 0;
  const { cx, cy, rx, ry } = useTweenedOval(
    {
      cx: landscapeHalfW,
      cy: cyTarget,
      rx: naturalRx * ovalScale,
      ry: naturalRy * ovalScale,
    },
    // Only live capture animates: the intro snaps into place beside
    // the parent's panel (no slide as the panel is first measured),
    // then the start of capture glides it to the center.
    landscapeMobile && !paused,
  );
  // Landscape: the free column left of the oval (and its ring), where
  // the feedback line and manual shutter go.
  const leftGutterW = Math.max(cx - rx - HOLD_RING_GAP - 24, 0);

  // Hold-timer ring color: the darkest shade of the brand palette, so
  // the arc is a deep version of the same hue as the glow and reads as
  // a different signal from it. The soft white halo under the arc
  // (see the ring's filter) and the neutral white track keep it
  // legible for customers whose brand is itself very dark.
  const primaryShades =
    theme.colors[theme.primaryColor] || theme.colors.blue;
  const holdRingColor =
    primaryShades[9] || primaryShades[primaryShades.length - 1] || primaryColor;

  // Inject/remove CSS
  useEffect(() => {
    injectStyles(primaryColor);
    return removeStyles;
  }, [primaryColor]);

  // Notify parent when models + camera are ready, passing camera signals
  useEffect(() => {
    if (modelsLoaded && cameraReady && onReady) {
      onReady(cameraSignalsRef.current);
    }
  }, [modelsLoaded, cameraReady, onReady]);

  // Propagate model load errors to parent
  useEffect(() => {
    if (modelLoadError && onError) {
      onError({ type: "model_load_error", message: modelLoadError?.message || "Failed to load face detection models" });
    }
  }, [modelLoadError, onError]);

  const captureCanvasRef = useRef(null);

  // One capture per mount, whichever path triggers it. Auto-capture can
  // fire in the same tick as a manual-shutter tap on a recovering stall
  // (~9 kid-prod sessions/day still got exactly 2 upload + estimation
  // dispatches after PR #382's button-only guard). Every parent page
  // unmounts this component once an upload starts and remounts it on
  // retry, so a per-mount latch covers all of them without touching
  // their upload handlers.
  const captureFiredRef = useRef(false);

  const handleCapture = useCallback(() => {
    if (!webcamRef.current) return;
    try {
      const video = webcamRef.current.video;
      if (!video) return;
      if (captureFiredRef.current) return;
      captureFiredRef.current = true;

      const vWidth = video.videoWidth;
      const vHeight = video.videoHeight;
      const frameChain = getFrameChain();
      // Conditions the capture decision was made on, plus how stale that
      // analysis is by the time the shutter actually fires. Filled in
      // below with a blur score measured on the captured pixels.
      const capturedAt = performance.now();
      const last = typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : null;
      const captureMetrics = last
        ? { ...last, frame_age_ms: Math.round(capturedAt - last.analysed_at) }
        : {};
      captureMetrics.landscape_flag = landscapeCapable;
      captureMetrics.orientation = currentOrientation();
      delete captureMetrics.analysed_at;

      if (vWidth > 0 && vHeight > 0) {
        // Capture at native video resolution using explicit canvas + toBlob
        let canvas = captureCanvasRef.current;
        if (!canvas) {
          canvas = document.createElement("canvas");
          captureCanvasRef.current = canvas;
        }
        canvas.width = vWidth;
        canvas.height = vHeight;
        const ctx = canvas.getContext("2d");
        // Sharpness of the current frame measured straight off the <video>,
        // the same way the hold loop measures every candidate. On Android
        // Chrome, drawImage from a <video> and from a full-resolution
        // canvas of the same frame do not produce the same 160px
        // downsample: the video path reads roughly half the Laplacian
        // variance of the canvas path (control-arm counterfactual,
        // 2026-09-16: best-vs-current median -570 on 98% of Android
        // captures, +122 on 89% of iOS). Comparing a video-measured best
        // against a canvas-measured current therefore never swapped on
        // Android. Both sides of the decision now come from the video.
        const currentVideoSharpness = measureSharpness(video);
        ctx.drawImage(video, 0, 0, vWidth, vHeight);
        // blur_var stays canvas-measured so it remains comparable with the
        // liveness curve and the SHARPNESS_TARGET derived from it.
        captureMetrics.blur_var = measureSharpness(canvas);
        captureMetrics.current_video_sharpness = currentVideoSharpness;

        // Best-frame capture (FACE_BEST_FRAME). The best gated frame from
        // the hold window replaces the current one when it is sharper.
        // Recorded in both arms so the same session shows what would
        // have been uploaded either way: the control arm logs the best
        // frame it did not use, the test arm logs the current frame it
        // replaced.
        const bestFrameOn = flagEnabled(FACE_BEST_FRAME);
        const best = typeof getBestFrame === "function" ? getBestFrame() : null;
        captureMetrics.best_frame_arm = bestFrameOn;
        if (best) {
          captureMetrics.best_frame_sharpness = best.sharpness;
          captureMetrics.best_frame_luma = best.luma;
          captureMetrics.best_frame_age_ms = best.age_ms;
          captureMetrics.frames_considered = best.considered;
          captureMetrics.current_frame_sharpness = captureMetrics.blur_var;
          const bestIsBetter =
            best.sharpness != null &&
            (currentVideoSharpness == null || best.sharpness > currentVideoSharpness) &&
            best.canvas.width === vWidth &&
            best.canvas.height === vHeight;
          captureMetrics.best_frame_used = bestFrameOn && bestIsBetter;
          if (bestFrameOn && bestIsBetter) {
            ctx.drawImage(best.canvas, 0, 0, vWidth, vHeight);
            captureMetrics.blur_var = measureSharpness(canvas);
          }
        } else {
          captureMetrics.best_frame_used = false;
        }

        canvas.toBlob(
          (blob) => {
            if (blob && onCapture) {
              trackEvent("face_captured", { ...captureMetrics, bytes: blob.size });
              const previewUrl = URL.createObjectURL(blob);
              onCapture(blob, previewUrl, frameChain);
            }
          },
          "image/jpeg",
          0.95
        );
      } else {
        // Fallback: use react-webcam screenshot, convert data URL to blob directly
        // NOTE: Do NOT use fetch(dataUrl) here — if the data URL is invalid or
        // the browser doesn't support data URL fetches, fetch() can resolve the
        // string as a relative URL and return the SPA's index.html instead of
        // image data, resulting in a corrupt upload.
        const imageSrc = webcamRef.current.getScreenshot();
        if (imageSrc && onCapture) {
          try {
            const [header, b64] = imageSrc.split(",");
            const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: mime });
            trackEvent("face_captured", { ...captureMetrics, fallback: "screenshot", bytes: blob.size });
            const previewUrl = URL.createObjectURL(blob);
            onCapture(blob, previewUrl, frameChain);
          } catch (e) {
            if (onError) onError({ type: "capture_error", message: "Failed to convert screenshot to blob" });
          }
        }
      }
    } catch (err) {
      if (onError) onError({ type: "capture_error", message: err?.message || "Screenshot capture failed" });
    }
  }, [onCapture, onError, getFrameChain, getLastFrameMetrics, getBestFrame, landscapeCapable]);

  // One-shot guard for the manual shutter. On the stalled devices this
  // button exists for, React re-renders lag behind a burst of taps and
  // the queued async toBlob callbacks all fire — without the guard each
  // tap ran the full upload + estimation dispatch (4-16 duplicate PASS
  // writes per session on kid-prod, 2026-09-11).
  const manualCaptureFiredRef = useRef(false);
  const [manualCaptureFired, setManualCaptureFired] = useState(false);
  const handleManualCapture = () => {
    if (manualCaptureFiredRef.current) return;
    manualCaptureFiredRef.current = true;
    setManualCaptureFired(true);
    handleCapture();
  };

  // Register auto-capture callback
  useEffect(() => {
    setOnAutoCapture(handleCapture);
  }, [handleCapture, setOnAutoCapture]);

  const cameraSignalsRef = useRef(null);

  const handleUserMedia = useCallback(() => {
    console.log("[VeratadCapture] handleUserMedia FIRED — camera access GRANTED");
    trackEvent("camera_permission_granted", {}, { once: true });
    cameraRespondedRef.current = true;
    const video = webcamRef.current?.video;
    if (!video) {
      console.warn("[VeratadCapture] handleUserMedia — webcamRef.current.video is null");
      return;
    }
    if (onCameraAllowed) onCameraAllowed();
    const checkReady = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
        // Collect camera signals once the track is active — virtual cameras
        // won't report facingMode, which the server can flag
        cameraSignalsRef.current = collectCameraSignals(video);
        trackEvent("camera_ready", {
          width: video.videoWidth,
          height: video.videoHeight,
          landscape_flag: landscapeCapable,
          orientation: currentOrientation(),
        });
        setCameraReady(true);
      } else {
        requestAnimationFrame(checkReady);
      }
    };
    requestAnimationFrame(checkReady);
  }, [onCameraAllowed, landscapeCapable]);

  const handleUserMediaError = useCallback(
    (err) => {
      console.error("[VeratadCapture] handleUserMediaError FIRED —", err?.name, err?.message, err);
      trackEvent("camera_permission_denied", { name: err?.name, error: err?.message });
      cameraRespondedRef.current = true;

      // On Android, a NotAllowedError without user interaction likely means
      // the OS-level Camera toggle is blocking. Show specific instructions.
      if (shouldShowAndroidCameraBlock() && err?.name === "NotAllowedError") {
        console.warn("[VeratadCapture] Android NotAllowedError — likely OS camera toggle or Chrome permission blocked");
        setCameraBlocked(true);
        setAndroidOsBlock(true);
        removeStyles();

        window.dispatchEvent(new CustomEvent("veratad-camera-error"));
        try {
          if (onError) {
            onError({
              type: "camera_error",
              name: err?.name,
              message: err?.message || "Camera access blocked by device",
            });
          }
        } catch (e) {
          console.error("[VeratadCapture] onError callback threw:", e);
        }
        if (typeof window.__veratadCameraError === "function") {
          window.__veratadCameraError();
        }
        return;
      }

      // Self-close: remove the portal immediately so the screen isn't blocked
      setCameraFailed(true);
      removeStyles();

      // Dispatch DOM event — this is the most reliable way to reach the parent.
      // Props callbacks and module-level globals have both proven unreliable
      // due to code-splitting and stale closure issues.
      window.dispatchEvent(new CustomEvent("veratad-camera-error"));

      // Also try props callback and global as additional channels
      try {
        if (onError) {
          onError({
            type: "camera_error",
            name: err?.name || "UnknownError",
            message: err?.message || "Camera access failed",
          });
        }
      } catch (e) {
        console.error("[VeratadCapture] onError callback threw:", e);
      }

      if (typeof window.__veratadCameraError === "function") {
        window.__veratadCameraError();
      }
    },
    [onError]
  );

  // Camera-fallback portal. Now renders for all devices — on Android it
  // shows the Quick Settings / Chrome permission instructions below the
  // primary camera-app button, on other devices it's just the primary
  // button + refresh. getAndroidBlockVariant() returns "generic" for
  // non-Android and the portal hides the Android-specific section.
  // (State names are legacy from when this was Android-only.)
  if (cameraBlocked && androidOsBlock) {
    return (
      <AndroidCameraBlockPortal
        locale={locale}
        requestId={requestId}
        captureType="face"
        isBrowserHandoffSession={false}
        androidVariant={getAndroidBlockVariant()}
        onPhotoFallback={onPhotoFallback}
        captureMode="user"
      />
    );
  }

  // Camera error: show error screen inside the portal instead of returning null.
  // This is self-contained — doesn't depend on any parent to handle the error.
  if (cameraFailed) {
    const errorPortal = (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <IconCameraOff size={64} color="rgba(255,255,255,0.7)" stroke={1.5} />
        <Text c="white" fz={22} fw={700} align="center" mt="lg">
          {locale?.errors?.cameraErrorTitle || "Camera Access Denied"}
        </Text>
        <Text c="rgba(255,255,255,0.7)" fz={14} align="center" mt="sm" maw={320}>
          {locale?.errors?.cameraDenied ||
            "You have denied camera permissions. Please go into settings and allow camera, refresh the page, and try again."}
        </Text>
        <Button
          variant="white"
          color="dark"
          size="md"
          mt="xl"
          onClick={() => {
            setCameraFailed(false);
            // Notify all parent communication channels
            window.dispatchEvent(new CustomEvent("veratad-camera-error"));
            try {
              if (onErrorRef.current) {
                onErrorRef.current({
                  type: "camera_error",
                  name: "NotAllowedError",
                  message: "Camera access denied by user",
                });
              }
            } catch (e) {
              console.error("[VeratadCapture] onError threw:", e);
            }
            if (typeof window.__veratadCameraError === "function") {
              window.__veratadCameraError();
            }
          }}
        >
          {locale?.errors?.goBack || "Go Back"}
        </Button>
      </div>
    );
    return ReactDOM.createPortal(errorPortal, document.body);
  }

  const portal = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        backgroundColor: "#000",
        overflow: "hidden",
      }}
    >
      {/* Center indicator — centered in oval. Three states:
          1. Paused: softly pulsing face icon (no spinner, no copy)
             so the screen reads as "ready, waiting for you" while
             the drawer overlay handles the actual call to action.
          2. Camera actively initializing (!paused, not ready yet):
             spinner + "Setting up your camera…" as before.
          3. Ready: nothing — the live video shows through. */}
      {paused ? (
        <div
          style={{
            position: "absolute",
            left: cx - rx,
            top: cy - ry,
            width: rx * 2,
            height: ry * 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            pointerEvents: "none",
            zIndex: 15,
          }}
        >
          {/* Tabler's FaceId glyph as the paused-state placeholder
              — instantly readable as "we're set up for face ID,
              waiting for permission." Wrapped in a span so the
              gentle opacity pulse can ride on it without touching
              the icon's internal stroke. */}
          <span
            style={{
              display: "inline-flex",
              color: "rgba(255,255,255,0.75)",
              animation: "veratadFacePulse 2.6s ease-in-out infinite",
            }}
          >
            <IconFaceId size={108} stroke={1.6} />
          </span>
          <style>{`
            @keyframes veratadFacePulse {
              0%, 100% { opacity: 0.6; }
              50%      { opacity: 1; }
            }
          `}</style>
        </div>
      ) : (!cameraReady || !modelsLoaded) && (
        <div
          style={{
            position: "absolute",
            left: cx - rx,
            top: cy - ry,
            width: rx * 2,
            height: ry * 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            pointerEvents: "none",
            zIndex: 15,
          }}
        >
          <Loader size={36} color="white" />
          <Text
            fw={500}
            c="white"
            fz={14}
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}
          >
            {locale?.capture?.settingUp || "Setting up your camera..."}
          </Text>
        </div>
      )}

      {/* Webcam layer — render only when not paused. react-webcam
          calls getUserMedia synchronously on mount, so the only way
          to defer the OS permission prompt until a user gesture is
          to defer the Webcam's mount itself. */}
      {!paused && (
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          screenshotQuality={1.0}
          videoConstraints={{
            facingMode: "user",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            aspectRatio: { ideal: 16 / 9 },
          }}
          mirrored
          onUserMedia={handleUserMedia}
          onUserMediaError={handleUserMediaError}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: cameraReady && modelsLoaded ? 1 : 0,
            transition: "opacity 0.3s ease-in",
          }}
        />
      )}

      {/* SVG overlay with oval cutout */}
      <svg
        width={vw}
        height={vh}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      >
        <defs>
          <mask id="face-oval-mask">
            <rect width="100%" height="100%" fill="white" />
            <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="black" />
          </mask>
        </defs>

        {/* Dark overlay with oval cutout */}
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.85)"
          mask="url(#face-oval-mask)"
        />

        {/* Oval border — stroke only, glow handled by HTML div below */}
        <ellipse
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          fill="none"
          stroke={passingCount === 0 ? "rgba(255,255,255,0.5)" : primaryColor}
          strokeWidth={isAllPassing ? 5 : passingCount >= 3 ? 4.5 : 3}
          style={{ transition: "stroke 0.4s, stroke-width 0.4s" }}
        />
      </svg>

      {/* Glow div — uses box-shadow instead of SVG drop-shadow for WebKit compat */}
      {(() => {
        const glowIntensity = passingCount === 0 ? 1 : passingCount + 1;
        const glowColorValue = passingCount === 0 ? "rgba(255,255,255,0.4)" : primaryColor;
        return (
          <div
            style={{
              position: "absolute",
              left: cx - rx,
              top: cy - ry,
              width: rx * 2,
              height: ry * 2,
              borderRadius: "50%",
              background: "transparent",
              pointerEvents: "none",
              zIndex: 10,
              '--glow-intensity': glowIntensity,
              '--glow-color': glowColorValue,
              animation: isAllPassing
                ? "glowLocked 1.5s ease-in-out infinite alternate"
                : "glowPulse 2s ease-in-out infinite",
              transition: "opacity 0.4s",
            }}
          />
        );
      })()}

      {/* Hold-timer ring. Only visible while every gate passes: a
          dim track outside the oval with an arc sweeping clockwise from
          12 o'clock over the same AUTO_CAPTURE_DELAY_MS the hook waits
          before firing the shutter, so the user can see how much longer
          to hold. Colored by holdRingColor, never the glow color, so it
          reads as a timer rather than more glow: the glow keeps saying
          "you're doing it right" and this ring says "how much longer".
          Unwinds when a gate breaks, matching the hook's timer reset. */}
      <HoldRing
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        width={vw}
        height={vh}
        active={isAllPassing}
        durationMs={AUTO_CAPTURE_DELAY_MS}
        color={holdRingColor}
        thickness={HOLD_RING_THICKNESS}
        gap={HOLD_RING_GAP}
        variant={HOLD_RING_VARIANT}
      />

      {/* Feedback text — suppressed when the parent is rendering
          the message in its own UI (e.g. AgeEstimation's live-mode
          drawer header) to avoid duplicate prompts, and while the
          manual shutter is up, since its hint replaces this line. */}
      {!hideInCameraFeedback && !detectionStalled && (
        <div
          style={{
            position: "absolute",
            ...(landscapeMobile
              ? {
                  top: cy,
                  transform: "translateY(-50%)",
                  left: 16,
                  width: leftGutterW,
                }
              : { top: cy + ry + FEEDBACK_OFFSET, left: 0, width: "100%" }),
            textAlign: "center",
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <span
            style={{
              color: "#fff",
              fontSize: isMobile ? 16 : 18,
              fontWeight: 500,
              textShadow: "0 1px 4px rgba(0,0,0,0.8)",
            }}
          >
            {cameraReady && modelsLoaded ? feedbackMessage : ""}
          </span>
        </div>
      )}

      {detectionStalled && cameraReady && (
        <div
          style={{
            position: "absolute",
            // With a parent drawer across the bottom, take the feedback
            // line's slot right under the oval (the oval scaling above
            // reserves the room); otherwise keep the historical
            // bottom-anchored placement.
            // In landscape there is no room under the oval: the hint
            // and button take the feedback line's place in the gutter
            // left of it.
            ...(landscapeMobile
              ? {
                  top: cy,
                  transform: "translateY(-50%)",
                  left: 16,
                  width: leftGutterW,
                }
              : {
                  ...(bottomInset > 0
                    ? { top: cy + ry + FEEDBACK_OFFSET - 8 }
                    : { bottom: isMobile ? 116 : 96 }),
                  left: 0,
                  width: "100%",
                }),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            zIndex: 30,
          }}
        >
          <span
            style={{
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              textShadow: "0 1px 4px rgba(0,0,0,0.8)",
              padding: "0 24px",
              textAlign: "center",
            }}
          >
            {locale?.feedback?.manualCaptureHint ||
              "Center your face in the oval and tap the button"}
          </span>
          <Button
            size="lg"
            radius="xl"
            variant="white"
            color="dark"
            loading={manualCaptureFired}
            onClick={handleManualCapture}
          >
            {locale?.feedback?.manualCaptureButton || "Take photo"}
          </Button>
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(portal, document.body);
}
